import React, { useMemo } from "react";
import CoinRow from "./CoinRow";
import CoinCard from "./CoinCard";
import SkeletonRow from "./SkeletonRow";
import "./styles/MarketsTable.css";
import "./styles/CoinCard.css";

const COLUMNS = [
  { key: "market_cap_rank", label: "#", className: "col-rank", sortable: true },
  { key: "watchlist", label: "", className: "col-star", sortable: false },
  { key: "name", label: "Coin", className: "col-name", sortable: false },
  { key: "current_price", label: "Price", className: "col-price num", sortable: true },
  {
    key: "price_change_percentage_1h_in_currency",
    label: "1h",
    className: "col-1h num",
    sortable: true,
    hideBelow: 1020,
  },
  {
    key: "price_change_percentage_24h_in_currency",
    label: "24h",
    className: "col-24h num",
    sortable: true,
  },
  {
    key: "price_change_percentage_7d_in_currency",
    label: "7d",
    className: "col-7d num",
    sortable: true,
    hideBelow: 1020,
  },
  { key: "market_cap", label: "Market Cap", className: "col-mcap num", sortable: true },
  { key: "total_volume", label: "Volume", className: "col-vol num", sortable: true },
  { key: "sparkline", label: "Last 7 Days", className: "col-spark", sortable: false },
];

/**
 * F3/F5/F7/F10 — top-50 table with sorting, skeleton loading,
 * empty states, and mobile card fallback (< 760px).
 */
export default function MarketsTable({
  coins,
  currency,
  watchlist,
  onToggleStar,
  onOpen,
  sort,
  onSort,
  loading,
  isCardLayout,
  error,
}) {
  const skeletonRows = useMemo(
    () => Array.from({ length: 10 }, (_, i) => <SkeletonRow key={i} />),
    []
  );

  const headerCells = useMemo(
    () =>
      COLUMNS.map((col) => {
        const active = sort.key === col.key;
        const ariaSort = active
          ? sort.dir === "asc"
            ? "ascending"
            : "descending"
          : col.sortable
            ? "none"
            : undefined;
        return (
          <th
            key={col.key}
            className={col.className}
            aria-sort={ariaSort}
            {...(col.sortable
              ? {
                  onClick: () => onSort(col.key),
                  "aria-label": `${col.label} — sort`,
                }
              : {})}
          >
            {col.label}
            {active && (
              <span className="sort-arrow" aria-hidden="true">
                {sort.dir === "asc" ? " ▲" : " ▼"}
              </span>
            )}
          </th>
        );
      }),
    [sort, onSort]
  );

  if (loading && !coins.length && !error) {
    return (
      <div className="table-wrap">
        <table className="markets-table" aria-busy="true">
          <thead>
            <tr>{headerCells}</tr>
          </thead>
          <tbody className="skel-tbody">{skeletonRows}</tbody>
        </table>
      </div>
    );
  }

  if (!coins.length) {
    return (
      <div className="empty-state" role="status">
        <div className="empty-icon" aria-hidden="true">◎</div>
        <p className="empty-title">
          {error ? "Couldn't load market data" : "No coins match your search"}
        </p>
        <p className="empty-hint">
          {error
            ? "Check your connection and try again — we'll keep any data already on screen."
            : "Try a different name or symbol, or clear the search to see all top coins."}
        </p>
      </div>
    );
  }

  if (isCardLayout) {
    return (
      <div className="coin-card-list">
        {coins.map((coin) => (
          <CoinCard
            key={coin.id}
            coin={coin}
            currency={currency}
            starred={watchlist.has(coin.id)}
            onToggleStar={onToggleStar}
            onOpen={onOpen}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="table-wrap">
      <table className="markets-table">
        <caption className="visually-hidden">
          Top 50 cryptocurrencies by market capitalization
        </caption>
        <thead>
          <tr>{headerCells}</tr>
        </thead>
        <tbody>
          {coins.map((coin) => (
            <CoinRow
              key={coin.id}
              coin={coin}
              currency={currency}
              starred={watchlist.has(coin.id)}
              onToggleStar={onToggleStar}
              onOpen={onOpen}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}
